// use-academy-progress — React-Wrapper für den Academy-Fortschritt pro User.
//
// Verantwortungen:
// 1. Fortschritts-Zeilen beim Mount vom Worker laden (auth-gated).
// 2. `markRead` / `saveQuiz` optimistisch im lokalen State updaten, dann POST.
//    POST-Fehler werden geloggt, nicht geworfen — optimistischer State bleibt.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchAcademyProgress,
  postAcademyProgress,
  type AcademyProgress,
} from '@/lib/academy-api';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';

export interface UseAcademyProgress {
  rows: readonly AcademyProgress[];
  loading: boolean;
  isRead: (moduleId: string) => boolean;
  quizPassed: (moduleId: string) => boolean;
  markRead: (moduleId: string) => Promise<void>;
  saveQuiz: (moduleId: string, passed: boolean, score: number) => Promise<void>;
}

export function useAcademyProgress(): UseAcademyProgress {
  const accessToken = useAuthStore(selectAccessToken);
  const [rows, setRows] = useState<readonly AcademyProgress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    fetchAcademyProgress(accessToken)
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch((e) => console.error('[academy] progress load failed', e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [accessToken]);

  const readSet = useMemo(
    () => new Set(rows.filter((r) => r.read_at).map((r) => r.module_id)),
    [rows],
  );
  const passedSet = useMemo(
    () => new Set(rows.filter((r) => r.quiz_passed).map((r) => r.module_id)),
    [rows],
  );

  const upsertLocal = useCallback((moduleId: string, patch: Partial<AcademyProgress>) => {
    setRows((prev) => {
      const existing = prev.find((r) => r.module_id === moduleId);
      const base: AcademyProgress = existing ?? {
        module_id: moduleId,
        read_at: null,
        quiz_passed: 0,
        quiz_score: null,
      };
      const next: AcademyProgress = { ...base, ...patch };
      return [next, ...prev.filter((r) => r.module_id !== moduleId)];
    });
  }, []);

  const markRead = useCallback(
    async (moduleId: string) => {
      upsertLocal(moduleId, { read_at: new Date().toISOString() });
      if (!accessToken) return;
      try {
        await postAcademyProgress({ moduleId, read: true }, accessToken);
      } catch (e) {
        console.error('[academy] markRead failed', e);
      }
    },
    [accessToken, upsertLocal],
  );

  const saveQuiz = useCallback(
    async (moduleId: string, passed: boolean, score: number) => {
      upsertLocal(moduleId, { quiz_passed: passed ? 1 : 0, quiz_score: score });
      if (!accessToken) return;
      try {
        await postAcademyProgress({ moduleId, quizPassed: passed, quizScore: score }, accessToken);
      } catch (e) {
        console.error('[academy] saveQuiz failed', e);
      }
    },
    [accessToken, upsertLocal],
  );

  const isRead = useCallback((moduleId: string) => readSet.has(moduleId), [readSet]);
  const quizPassed = useCallback((moduleId: string) => passedSet.has(moduleId), [passedSet]);

  return { rows, loading, isRead, quizPassed, markRead, saveQuiz };
}
